// utils/timesheet.ts
import moment from "moment";
import {
  MonthOption,
  TimesheetData,
  TimesheetStats,
} from "../interface/dashboard";

// Generate month options for the last 12 months
export const generateMonthOptions = (): MonthOption[] => {
  const options: MonthOption[] = [];
  const currentDate = moment();

  // Add "This Month" option
  options.push({
    value: "current",
    label: "This Month",
    startDate: currentDate.clone().startOf("month").format("YYYY-MM-DD"),
    endDate: currentDate.clone().endOf("month").format("YYYY-MM-DD"),
  });

  // Add "Last Month" option
  options.push({
    value: "last",
    label: "Last Month",
    startDate: currentDate
      .clone()
      .subtract(1, "month")
      .startOf("month")
      .format("YYYY-MM-DD"),
    endDate: currentDate
      .clone()
      .subtract(1, "month")
      .endOf("month")
      .format("YYYY-MM-DD"),
  });

  // Add previous months
  for (let i = 2; i <= 12; i++) {
    const date = currentDate.clone().subtract(i, "months");
    options.push({
      value: date.format("YYYY-MM"),
      label: date.format("MMMM YYYY"),
      startDate: date.startOf("month").format("YYYY-MM-DD"),
      endDate: date.endOf("month").format("YYYY-MM-DD"),
    });
  }

  return options;
};

// Calculate aggregated stats from timesheet data
export const calculateTimesheetStats = (
  data: TimesheetData[]
): TimesheetStats => {
  const totalHours = data.reduce((sum, item) => sum + item.totalHours, 0);
  const totalOvertimeHours = data.reduce(
    (sum, item) => sum + item.overtimeHours,
    0
  );
  const totalCount = data.reduce((sum, item) => sum + item.count, 0);

  return {
    totalHours,
    totalOvertimeHours,
    totalCount,
    averageHoursPerDay: data.length > 0 ? totalHours / data.length : 0,
    averageOvertimePerDay:
      data.length > 0 ? totalOvertimeHours / data.length : 0,
  };
};

// Format hours for display
export const formatHours = (hours: number): string => {
  return hours.toFixed(2);
};

export function formatHoursToText(decimalHours) {
  if (!decimalHours || decimalHours === 0) {
    return "0 hours";
  }

  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);

  if (hours === 0) {
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  }

  if (minutes === 0) {
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }

  return `${hours} ${hours === 1 ? "hour" : "hours"} ${minutes} ${
    minutes === 1 ? "minute" : "minutes"
  }`;
}

// Format date for display
export const formatDisplayDate = (dateString: string): string => {
  return moment(dateString).format("MMM DD, YYYY");
};

// Prepare chart data
export const prepareChartData = (data: TimesheetData[]) => {
  const categories = data.map((item) => formatDisplayDate(item.date));

  const series = [
    {
      name: "Total Hours",
      data: data.map((item) => parseFloat(formatHoursToText(item.totalHours))),
    },
    {
      name: "Overtime Hours",
      data: data.map((item) =>
        parseFloat(formatHoursToText(item.overtimeHours))
      ),
    },
  ];

  return { categories, series };
};
